// BlocklySettingsModal - Blockly ブロック設定モーダル

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Backdrop, Fade } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ExtensionIcon from "@mui/icons-material/Extension";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { actions } from "features/builder/slice";
import { BuilderBlockId } from "features/builder/types";
import { updatePieceBlockly } from "features/builder/api";

interface BlockOption {
  id: BuilderBlockId;
  labelKey: string;
  category: string;
}

const BLOCK_OPTIONS: BlockOption[] = [
  // うごき
  {
    id: BuilderBlockId.MOVE_FORWARD,
    labelKey: "MSG_BUILDER_BLOCK_MOVE_FORWARD",
    category: "motion",
  },
  { id: BuilderBlockId.TURN_LEFT, labelKey: "MSG_BUILDER_BLOCK_TURN_LEFT", category: "motion" },
  { id: BuilderBlockId.TURN_RIGHT, labelKey: "MSG_BUILDER_BLOCK_TURN_RIGHT", category: "motion" },
  { id: BuilderBlockId.JUMP, labelKey: "MSG_BUILDER_BLOCK_JUMP", category: "motion" },
  // くりかえし
  { id: BuilderBlockId.LOOP, labelKey: "MSG_BUILDER_BLOCK_LOOP", category: "control" },
  { id: BuilderBlockId.IF_COLOR, labelKey: "MSG_BUILDER_BLOCK_IF_COLOR", category: "control" },
  {
    id: BuilderBlockId.WHILE_COLOR,
    labelKey: "MSG_BUILDER_BLOCK_WHILE_COLOR",
    category: "control",
  },
];

const CATEGORIES = [
  { id: "motion", labelKey: "MSG_BUILDER_BLOCKLY_CATEGORY_MOTION" },
  { id: "control", labelKey: "MSG_BUILDER_BLOCKLY_CATEGORY_CONTROL" },
];

export const BlocklySettingsModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch();

  const storeBlocks = useSelector((state: RootState) => state.builder.enabledBlocks);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const active = useSelector((state: RootState) => state.user.active);

  // 開くたびに slice の enabledBlocks で初期化（spec.md §5.8.7）
  const [enabledBlocks, setEnabledBlocks] = useState<Set<BuilderBlockId>>(new Set(storeBlocks));

  useEffect(() => {
    if (open) setEnabledBlocks(new Set(storeBlocks));
  }, [open, storeBlocks]);

  const handleClose = () => {
    play();
    btnDelay(() => onClose());
  };

  const handleToggleBlock = (blockId: BuilderBlockId) => {
    play();
    setEnabledBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) {
        next.delete(blockId);
      } else {
        next.add(blockId);
      }
      return next;
    });
  };

  const handleSave = () => {
    play();
    btnDelay(async () => {
      const blocks = Array.from(enabledBlocks);
      dispatch(actions.setEnabledBlocks(blocks));
      await updatePieceBlockly({
        piece_id: pieceId,
        uid: active.uid,
        enabled_blocks: blocks,
      });
      onClose();
    });
  };

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: 100000,
        backgroundColor: "transparent",
        backdropFilter: "blur(4px)",
      }}
    >
      <Fade in={open}>
        <div
          className="flex-col-el flex-center w-full h-full bg-textcolor-black/50"
          onClick={handleClose}
        >
          <div
            className="flex-col-el"
            style={{ transform: `scale(${scale})` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex-col-el w-[480px] bg-white rounded-2xl border-4 border-white shadow-2xl overflow-hidden">
              {/* ヘッダー */}
              <div className="flex-row-el items-center gap-3 px-6 py-4 bg-purple-500">
                <ExtensionIcon className="text-white" style={{ fontSize: 32 }} />
                <h2 className="text-white text-2xl">
                  {I18n.t("MSG_BUILDER_BLOCKLY_SETTINGS_TITLE")}
                </h2>
              </div>

              {/* コンテンツ */}
              <div className="flex-col-el gap-4 p-6 max-h-[400px] overflow-y-auto">
                {CATEGORIES.map((category) => (
                  <div key={category.id} className="flex-col-el gap-2">
                    <h3 className="text-gray-700 text-lg">{I18n.t(category.labelKey)}</h3>
                    <div className="flex-col-el gap-1">
                      {BLOCK_OPTIONS.filter((b) => b.category === category.id).map((block) => {
                        const isEnabled = enabledBlocks.has(block.id);
                        return (
                          <button
                            key={block.id}
                            onClick={() => handleToggleBlock(block.id)}
                            className={`flex-row-el items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all active:scale-[0.98] ${
                              isEnabled
                                ? "bg-purple-50 border-purple-300"
                                : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                            }`}
                          >
                            {isEnabled ? (
                              <CheckBoxIcon className="text-purple-500" style={{ fontSize: 24 }} />
                            ) : (
                              <CheckBoxOutlineBlankIcon
                                className="text-gray-400"
                                style={{ fontSize: 24 }}
                              />
                            )}
                            <span
                              className={`text-lg ${
                                isEnabled ? "text-purple-700" : "text-gray-600"
                              }`}
                            >
                              {I18n.t(block.labelKey)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* フッター */}
              <div className="flex-row-el justify-between items-center px-6 py-4 bg-gray-50">
                <span className="text-gray-500 text-sm">
                  {I18n.t("MSG_BUILDER_BLOCKLY_SELECTED_COUNT", {
                    count: enabledBlocks.size,
                  })}
                </span>
                <div className="flex-row-el gap-3">
                  <button
                    onClick={handleClose}
                    className="px-6 py-2 text-gray-600 text-lg bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-100 active:scale-95 transition-all"
                  >
                    {I18n.t("MSG_CLOSE_BTN")}
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-8 py-2 text-white text-lg bg-purple-500 rounded-xl hover:bg-purple-600 active:scale-95 transition-all"
                  >
                    {I18n.t("MSG_OK_BTN")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fade>
    </Backdrop>
  );
};
